'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { geoMercator, geoNaturalEarth1, geoPath, type GeoProjection } from 'd3-geo'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import { Loader2, MapPin } from 'lucide-react'
import { apiGet } from './Entries/entriesApi'
import { RetryBanner } from './EntryDetail/SibsWidgets'
import { buildDistrictIndex, type DistrictProps } from './geo/placeNames'

// Where a dashboard's entries came from — Cloudflare's request geo on
// raw_entries (city/region/country as text), drawn as a filled map from the
// names alone: Portugal by district (Azores and Madeira as insets), and the
// world by country as the second layer. No coordinates exist or are needed.
//
// Self-contained so any dashboard can drop it in: it fetches
// /api/entries/entry-locations with the dashboard's own key and scope.
// Hono decides what that key may see (a fixed scope for bespoke dashboards
// like sibs-mail; owned platform_ids for the per-brand templates).

export type EntryLocationRow = {
  country: string | null
  region: string | null
  city: string | null
  continent: string | null
  entries: number
  scams: number
}

type CountryProps = { iso: string; name: string }
type Shapes = {
  districts: FeatureCollection<Geometry, DistrictProps>
  world: FeatureCollection<Geometry, CountryProps>
}

// Loaded on first render of any map, once per page — the two GeoJSON files
// are ~58 KB gzipped together and stay out of the main bundle.
let shapesPromise: Promise<Shapes> | null = null
function loadShapes(): Promise<Shapes> {
  shapesPromise ??= Promise.all([
    import('./geo/pt-districts.geo.json'),
    import('./geo/world-110m.geo.json'),
  ]).then(([d, w]) => ({
    districts: (d.default ?? d) as unknown as Shapes['districts'],
    world: (w.default ?? w) as unknown as Shapes['world'],
  })).catch((err) => {
    shapesPromise = null
    throw err
  })
  return shapesPromise
}

// Sequential blue ramp (dataviz reference palette), one step per decade of
// entries: 1-9, 10-99, 100-999, 1k-9.9k, 10k+. Decades rather than an even
// split because the counts are this skewed (one district can hold most of a
// month). Dark mode has its own steps, light-on-dark for "more".
const BIN_FLOORS = [1, 10, 100, 1000, 10000]
const RAMP_LIGHT = ['#cde2fb', '#86b6ef', '#3987e5', '#1c5cab', '#0d366b']
const RAMP_DARK = ['#104281', '#1c5cab', '#2a78d6', '#6da7ec', '#b7d3f6']

const binOf = (n: number) => {
  let i = -1
  for (let k = 0; k < BIN_FLOORS.length; k++) if (n >= BIN_FLOORS[k]) i = k
  return i
}
const binLabel = (k: number) => {
  const lo = BIN_FLOORS[k]
  const hi = BIN_FLOORS[k + 1]
  const fmt = (v: number) => (v >= 1000 ? `${v / 1000}k` : String(v))
  return hi ? `${fmt(lo)}–${fmt(hi - 1)}` : `${fmt(lo)}+`
}

type Area = { key: string; label: string; entries: number; scams: number; cities: { name: string; entries: number }[] }

function addTo(map: Map<string, Area>, key: string, label: string, row: EntryLocationRow) {
  const a = map.get(key) ?? { key, label, entries: 0, scams: 0, cities: [] }
  a.entries += row.entries
  a.scams += row.scams
  if (row.city) a.cities.push({ name: row.city, entries: row.entries })
  map.set(key, a)
}

// Map geometry, in viewBox units. Portugal: mainland on the right, the two
// archipelagos as boxed insets on the left (their real positions would leave
// the mainland a sliver).
const PT_W = 560
const PT_H = 440
const PT_MAINLAND: [[number, number], [number, number]] = [[300, 12], [548, 428]]
const PT_AZORES: [[number, number], [number, number]] = [[18, 34], [270, 186]]
const PT_MADEIRA: [[number, number], [number, number]] = [[18, 238], [150, 352]]
const INSET_PAD = 10
const WORLD_W = 960
const WORLD_H = 470

type Hover = { key: string; x: number; y: number }

export function EntryLocationMap({
  dashboard,
  since,
  until,
  platformIds,
  sources,
  isDark,
  className = '',
  title = 'Where entries came from',
}: {
  /** The rendering dashboard's view key — Hono authorizes and scopes by it. */
  dashboard: string
  since: string
  until?: string
  platformIds?: number[]
  sources?: string[]
  isDark: boolean
  className?: string
  title?: string
}) {
  const [rows, setRows] = useState<EntryLocationRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retryTick, setRetryTick] = useState(0)
  const [shapes, setShapes] = useState<Shapes | null>(null)
  const [shapesError, setShapesError] = useState<string | null>(null)
  const [layer, setLayer] = useState<'pt' | 'world'>('pt')
  const [hover, setHover] = useState<Hover | null>(null)
  const mapBoxRef = useRef<HTMLDivElement>(null)

  const platformKey = platformIds?.join(',') ?? ''
  const sourceKey = sources?.join(',') ?? ''

  useEffect(() => {
    let cancelled = false
    loadShapes().then((s) => { if (!cancelled) setShapes(s) }).catch((e) => { if (!cancelled) setShapesError(String(e)) })
    return () => { cancelled = true }
  }, [retryTick])

  useEffect(() => {
    let cancelled = false
    const params = new URLSearchParams({ dashboard, since })
    if (until) params.set('until', until)
    if (platformKey) params.set('platform_ids', platformKey)
    if (sourceKey) params.set('sources', sourceKey)
    setLoading(true)
    setError(null)
    apiGet<EntryLocationRow[]>(`/entry-locations?${params.toString()}`).then((res) => {
      if (cancelled) return
      if (res.success) setRows(res.data ?? [])
      else setError(res.error ?? 'Could not load entry locations')
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [dashboard, since, until, platformKey, sourceKey, retryTick])

  const districtOf = useMemo(
    () => (shapes ? buildDistrictIndex(shapes.districts.features.map((f) => f.properties)) : null),
    [shapes],
  )

  const agg = useMemo(() => {
    const districts = new Map<string, Area>()
    const countries = new Map<string, Area>()
    const places: { key: string; label: string; sub: string; entries: number; country: string | null }[] = []
    let total = 0, inPt = 0, unmappedPt = 0, noLocation = 0
    const countryName = new Map((shapes?.world.features ?? []).map((f) => [f.properties.iso, f.properties.name]))
    for (const r of rows ?? []) {
      total += r.entries
      if (!r.country) { noLocation += r.entries; continue }
      addTo(countries, r.country, countryName.get(r.country) ?? r.country, r)
      places.push({
        key: `${r.country}|${r.region ?? ''}|${r.city ?? ''}`,
        label: r.city ?? r.region ?? r.country,
        sub: [r.city ? r.region : null, r.country].filter(Boolean).join(', '),
        entries: r.entries,
        country: r.country,
      })
      if (r.country === 'PT') {
        inPt += r.entries
        const iso = districtOf?.(r.region) ?? null
        if (iso) addTo(districts, iso, r.region ?? iso, r)
        else unmappedPt += r.entries
      }
    }
    for (const a of [...districts.values(), ...countries.values()]) a.cities.sort((x, y) => y.entries - x.entries)
    places.sort((a, b) => b.entries - a.entries)
    return { districts, countries, places, total, inPt, unmappedPt, noLocation, elsewhere: total - inPt - noLocation }
  }, [rows, districtOf, shapes])

  const ramp = isDark ? RAMP_DARK : RAMP_LIGHT
  const emptyFill = 'var(--secondary)'
  const surface = 'var(--card)'
  const card = 'border-border bg-card'
  const muted = 'text-subtle'
  const text = 'text-foreground'
  const sub = 'text-muted-foreground'
  const rail = 'bg-secondary'
  const fillFor = (n: number) => (n > 0 ? ramp[binOf(n)] : emptyFill)

  const areas = layer === 'pt' ? agg.districts : agg.countries
  const maxArea = Math.max(0, ...[...areas.values()].map((a) => a.entries))
  const usedBins = maxArea > 0 ? BIN_FLOORS.map((_, k) => k).filter((k) => k <= binOf(maxArea)) : []

  // Paths for the active layer, recomputed only when the shapes or layer change.
  const drawn = useMemo(() => {
    if (!shapes) return null
    if (layer === 'world') {
      const features = shapes.world.features.filter((f) => f.properties.iso !== 'AQ')
      const projection = geoNaturalEarth1().fitSize([WORLD_W, WORLD_H], { type: 'FeatureCollection', features })
      const path = geoPath(projection)
      return {
        frames: [] as { x: number; y: number; w: number; h: number; label: string }[],
        paths: features.map((f) => ({ key: f.properties.iso, label: f.properties.name, d: path(f) ?? '' })),
      }
    }
    const all = shapes.districts.features
    const group = (isos: string[] | null) =>
      all.filter((f) => (isos ? isos.includes(f.properties.iso) : !['PT-20', 'PT-30'].includes(f.properties.iso)))
    const fit = (features: Feature<Geometry, DistrictProps>[], box: [[number, number], [number, number]], pad: number): GeoProjection =>
      geoMercator().fitExtent([[box[0][0] + pad, box[0][1] + pad], [box[1][0] - pad, box[1][1] - pad]], { type: 'FeatureCollection', features })
    const parts = [
      { features: group(null), box: PT_MAINLAND, pad: 0, label: '' },
      { features: group(['PT-20']), box: PT_AZORES, pad: INSET_PAD, label: 'Açores' },
      { features: group(['PT-30']), box: PT_MADEIRA, pad: INSET_PAD, label: 'Madeira' },
    ]
    return {
      frames: parts.filter((p) => p.label).map((p) => ({ x: p.box[0][0], y: p.box[0][1], w: p.box[1][0] - p.box[0][0], h: p.box[1][1] - p.box[0][1], label: p.label })),
      paths: parts.flatMap((p) => {
        const path = geoPath(fit(p.features, p.box, p.pad))
        return p.features.map((f) => ({ key: f.properties.iso, label: f.properties.name, d: path(f) ?? '' }))
      }),
    }
  }, [shapes, layer])

  const onMove = (key: string) => (ev: React.MouseEvent) => {
    const box = mapBoxRef.current?.getBoundingClientRect()
    if (!box) return
    setHover({ key, x: ev.clientX - box.left, y: ev.clientY - box.top })
  }

  const hovered = hover ? areas.get(hover.key) : null
  const hoveredLabel = hover ? drawn?.paths.find((p) => p.key === hover.key)?.label ?? hover.key : ''
  const listPlaces = (layer === 'pt' ? agg.places.filter((p) => p.country === 'PT') : agg.places).slice(0, 12)
  const maxPlace = Math.max(1, ...listPlaces.map((p) => p.entries))
  const shownError = error ?? shapesError

  return (
    <div className={`flex min-w-0 flex-col overflow-hidden border px-4 py-3 ${card} ${className}`}>
      <div className="mb-2 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Request location</div>
          <div className={`mt-1 truncate text-base font-semibold tracking-tight ${text}`}>{title}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex flex-wrap gap-x-3 font-mono text-[10px] ${sub}`}>
            <span><span className={text}>{agg.inPt}</span> Portugal</span>
            <span><span className={text}>{agg.elsewhere}</span> elsewhere</span>
            <span><span className={text}>{agg.noLocation}</span> no location</span>
            {agg.unmappedPt > 0 && <span title="Portuguese entries whose region name matched no district"><span className={text}>{agg.unmappedPt}</span> unmapped</span>}
          </div>
          <div className={`flex overflow-hidden rounded-md border font-mono text-[10px] uppercase tracking-widest border-border`} role="tablist">
            {(['pt', 'world'] as const).map((l) => (
              <button
                key={l}
                role="tab"
                aria-selected={layer === l}
                onClick={() => { setLayer(l); setHover(null) }}
                className={`px-2.5 py-1 transition-colors ${layer === l
                  ? ('bg-signal-surface text-signal')
                  : ('text-muted-foreground hover:bg-secondary')}`}
              >
                {l === 'pt' ? 'Portugal' : 'World'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {shownError ? (
        <RetryBanner message={shownError} onRetry={() => { setShapesError(null); setRetryTick((t) => t + 1) }} retrying={loading} isDark={isDark} />
      ) : (
        <div className="flex min-h-0 flex-1 gap-4">
          <div ref={mapBoxRef} className="relative min-h-0 min-w-0 flex-[3]" onMouseLeave={() => setHover(null)}>
            {!drawn || rows === null ? (
              <div className={`flex h-full items-center justify-center gap-2 font-mono text-[11px] ${muted}`}>
                <Loader2 className="h-4 w-4 animate-spin" /> Loading map
              </div>
            ) : (
              <svg
                viewBox={layer === 'pt' ? `0 0 ${PT_W} ${PT_H}` : `0 0 ${WORLD_W} ${WORLD_H}`}
                className="h-full w-full"
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={`${title}: ${layer === 'pt' ? 'Portugal by district' : 'world by country'}`}
              >
                {drawn.frames.map((f) => (
                  <g key={f.label}>
                    <rect x={f.x} y={f.y} width={f.w} height={f.h} rx={6} fill="none" stroke={'var(--border)'} strokeWidth={1} />
                    <text x={f.x + 8} y={f.y + 16} className="font-mono" fontSize={10} fill={'var(--subtle-text)'}>{f.label}</text>
                  </g>
                ))}
                {drawn.paths.map((p) => {
                  const n = areas.get(p.key)?.entries ?? 0
                  const isHover = hover?.key === p.key
                  return (
                    <path
                      key={p.key}
                      d={p.d}
                      fill={fillFor(n)}
                      stroke={isHover ? ('var(--foreground)') : surface}
                      strokeWidth={isHover ? 1.5 : layer === 'pt' ? 1 : 0.5}
                      strokeLinejoin="round"
                      onMouseMove={onMove(p.key)}
                      onMouseEnter={onMove(p.key)}
                      style={{ cursor: 'default', transition: 'fill 300ms' }}
                    />
                  )
                })}
              </svg>
            )}

            {loading && rows !== null && (
              <div className={`absolute right-1 top-1 flex items-center gap-1 font-mono text-[10px] ${muted}`}>
                <Loader2 className="h-3 w-3 animate-spin" /> updating
              </div>
            )}

            {hover && (
              <div
                className={`pointer-events-none absolute z-10 min-w-40 rounded-md border px-3 py-2 text-[12px] shadow-notice ${isDark ? 'border-border bg-[#111c28] text-foreground' : 'border-border bg-card text-foreground'}`}
                style={{ left: Math.min(hover.x + 14, (mapBoxRef.current?.clientWidth ?? 0) - 180), top: hover.y + 14 }}
              >
                <div className="font-semibold">{hoveredLabel}</div>
                <div className={`font-mono text-[11px] ${sub}`}>
                  {hovered ? <>{hovered.entries} entries · {hovered.scams} scam</> : 'no entries'}
                </div>
                {hovered && hovered.cities.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {hovered.cities.slice(0, 4).map((c) => (
                      <div key={c.name} className="flex justify-between gap-3 text-[11px]">
                        <span className="truncate">{c.name}</span>
                        <span className="font-mono tabular-nums">{c.entries}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {usedBins.length > 0 && (
              <div className={`absolute bottom-0 left-0 flex items-center gap-2 font-mono text-[9px] ${sub}`} aria-label="Entries per area">
                {usedBins.map((k) => (
                  <span key={k} className="flex items-center gap-1">
                    <span className="inline-block h-2.5 w-3.5 rounded-sm" style={{ backgroundColor: ramp[k] }} />
                    {binLabel(k)}
                  </span>
                ))}
                <span className="flex items-center gap-1">
                  <span className={`inline-block h-2.5 w-3.5 rounded-sm border border-border`} style={{ backgroundColor: emptyFill }} />
                  none
                </span>
              </div>
            )}
          </div>

          {/* The same numbers as a list — top places at city level, which
              the filled map can't show. */}
          <div className="flex min-h-0 min-w-0 flex-[2] flex-col">
            <div className={`mb-1 font-mono text-[9px] uppercase tracking-widest ${muted}`}>
              Top places{layer === 'pt' ? ' in Portugal' : ''}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-1">
              {rows !== null && listPlaces.length === 0 ? (
                <div className={`flex flex-1 items-center justify-center gap-2 border border-dashed font-mono text-[11px] border-border text-muted-foreground`}>
                  <MapPin className="h-3.5 w-3.5" /> No located entries in range
                </div>
              ) : listPlaces.map((p) => (
                <div key={p.key} className="px-1">
                  <div className="flex items-baseline gap-2">
                    <span className={`min-w-0 truncate text-[13px] font-semibold ${text}`}>{p.label}</span>
                    <span className={`min-w-0 flex-1 truncate text-[11px] ${sub}`}>{p.sub}</span>
                    <span className={`shrink-0 font-mono text-[12px] tabular-nums ${text}`}>{p.entries}</span>
                  </div>
                  <div className={`mt-0.5 h-1 rounded-full ${rail}`}>
                    <div className="h-1 rounded-full" style={{ width: `${Math.max(3, (p.entries / maxPlace) * 100)}%`, backgroundColor: ramp[isDark ? 3 : 2] }} />
                  </div>
                </div>
              ))}
            </div>
            <div className={`mt-1 shrink-0 text-[10px] leading-snug ${muted}`}>
              Where the request came from (Cloudflare, by IP), not the person&apos;s address. Entries sent by our own services carry the server&apos;s location.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
